import axios from 'axios';
import React, { useEffect, useState } from 'react';
import {Button, Card, CardContent, Grid, Tabs, Tab, Box, FormControl, InputLabel, MenuItem, Select, Avatar, Typography, Link, Badge } from '@mui/material';
import { NavLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import RouteIcon from '@mui/icons-material/Route';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddIcCallIcon from '@mui/icons-material/AddIcCall';
import KeyIcon from '@mui/icons-material/Key';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import { Collect } from '../../../models/collect';
import { SelectChangeEvent } from '@mui/material/Select'; // Importa SelectChangeEvent
import useAxiosInterceptor from '../../AxiosInterceptor';
import MyModal from '../../../components/modales/modal_pagos';
import Invoice from './modal/invoice';
import { Modal, TextField } from '@mui/material';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const RutasOffList: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [office_id, setOffice_id] = useState('');
  const [collects, setCollects] = useState<string>('');
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]); // Estado para rutas activas
  const [pendingRoutes, setPendingRoutes] = useState<Route[]>([]); // Estado para rutas pendientes
  const [completedRoutes, setCompletedRoutes] = useState<Route[]>([]); // Estado para rutas completadas
  const [routeId, setRouteId] = useState(''); // Mantener la dependencia adecuada para reconsultar
  const [tabIndex, setTabIndex] = useState(0); // Estado para controlar el tab seleccionado
  // Estados para el modal de pagos
  const [modalOpen, setModalOpen] = useState(false); // Estado para abrir/cerrar el modal
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null); // Estado para guardar el id del pago seleccionado
  const [modalInvoiceOpen, setModalInvoiceOpen] = useState(false); // Estado para controlar el modal de Invoice
  const [clientFullName, setClientFullName] = useState('');
  useAxiosInterceptor();
  const handleOpenInvoiceModal = (fullName: string, payId: number) => {
    setClientFullName(fullName);
    setSelectedPaymentId(payId); // <-- Guarda el payId
    setModalInvoiceOpen(true);
  };
  const handleCloseInvoiceModal = () => {
    setModalInvoiceOpen(false);
  };
  const style = {
    width: isMobile ? '90%' : '60%', // Ajusta el ancho dependiendo si es mobile o no
    maxWidth: '350px', // Ancho máximo para pantallas grandes
    height: isMobile ? 'auto' : '75%', // Ajusta la altura automáticamente en mobile y usa un porcentaje fijo para pantallas más grandes
    maxHeight: '90vh', // Altura máxima para evitar que se salga de la pantalla
    overflowY: 'auto', // Añade scroll si el contenido excede la altura
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 2,
    borderRadius: 2,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };

  // Lógica para obtener oficinas y cobros
  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const officesResponse = axios.get('offices');
        const collectsOfficeResponse = office_id ? axios.get(`collectsoffice/${office_id}`) : null;
        const routeCollectResponse = collects ? axios.get(`routecollect/${collects}`) : null;

        const [officesData, collectsOfficeData, routeCollectData] = await Promise.all([
          officesResponse,
          collectsOfficeResponse,
          routeCollectResponse,
        ]);

        if (routeCollectData && routeCollectData.data.data && routeCollectData.data.data.length > 0) {
          const routeCollectArray = routeCollectData.data.data;
          const routeCollectId = routeCollectArray[0].id;
          setRouteId(routeCollectId); // Asegúrate de que el routeId se actualiza correctamente para disparar el siguiente useEffect
        }
        if (officesData) setOffices(officesData.data.data);
        if (collectsOfficeData) setCollectsOffice(collectsOfficeData.data);

        await new Promise((resolve) => setTimeout(resolve, 1000));
        showAlert('Se ha realizado la consulta exitosamente!', 'info');
      } catch (error) {
        //console.log('Error fetching data:', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, [collects, office_id]); // Dependencias: solo vuelve a ejecutarse si cambian `collects` o `office_id`.

  // Lógica para obtener las rutas cuando cambia el "Cobro" o "Oficina"
  useEffect(() => {
    if (routeId.length !== 0) {
      const fetchRoutes = async () => {
        showLoading();
        try {
          const { data } = await axios.get(`routes/${routeId}`);

          // Separar rutas por estado
          // Filtra las rutas activas (cuando el estado es 0 o false)
          const activeRoutes = data.data.filter((route: Route) => Number(route.lend.state) === 0);

          // Filtra las rutas completadas (cuando el estado es 1 o true)
          const completedRoutes = data.data.filter((route: Route) => Number(route.lend.state) === 1);

          setRoutes(activeRoutes); // Asigna rutas activas
          setCompletedRoutes(completedRoutes); // Asigna rutas completadas

          await new Promise((resolve) => setTimeout(resolve, 1000));
          showAlert('Se ha realizado la consulta exitosamente!', 'info');
        } catch (error) {
          console.error('Error fetching data: ', error);
          showAlert('Error fetching data!', 'error');
        } finally {
          hideLoading();
        }
      };
      fetchRoutes();
    }
  }, [routeId]); // Dependencia: `routeId` se actualiza correctamente al cambiar `collects` o `office_id`

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Función para mover una ruta a la pestaña de "Pendiente"
  const moveToPending = (route: Route) => {
    setRoutes((prevRoutes) => prevRoutes.filter((r) => r.lend.id !== route.lend.id)); // Elimina la ruta de las activas
    setPendingRoutes((prevPending) => [...prevPending, route]); // Añade la ruta a pendientes
  };

  // Función para limpiar las tarjetas cuando cambia el valor de "Oficina" o "Cobro"
  const clearRoutes = () => {
    setRoutes([]); // Limpiar las rutas activas
    setPendingRoutes([]); // Limpiar las rutas pendientes
    setCompletedRoutes([]); // Limpiar las rutas completadas
    setRouteId(''); // Importante limpiar también `routeId` para evitar datos obsoletos
  };

  // Cuando cambie el valor de "Oficina" o "Cobro", limpiar las rutas y volver a consultar las nuevas rutas.
  const handleOfficeChange = (event: SelectChangeEvent<string>) => {
    setOffice_id(event.target.value);
    clearRoutes(); // Limpiar las tarjetas antes de consultar nuevamente
  };

  const handleCollectChange = (event: SelectChangeEvent<string>) => {
    setCollects(event.target.value);
    clearRoutes(); // Limpiar las tarjetas antes de consultar nuevamente
  };

  // Función para abrir el modal
  const handleOpenModal = (id: number) => {
    setSelectedPaymentId(id); // Guardamos el id del pago seleccionado
    setModalOpen(true); // Abrimos el modal
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setModalOpen(false); // Cerramos el modal
  };
  function obtenerFechaActual() {
    return nowOperationalDateString();
  }
  
  
  return (
    <HeaderResponsive >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Generar Rutas</Typography>
        <div style={{ marginLeft: "auto" }}>
          <Button
            variant="contained"
            startIcon={isMobile ? null : <RouteIcon />}
            component={NavLink}
            to="/prestamos/abonos/add"
          >
            {isMobile ? <RouteIcon /> : "Generar"}
          </Button>
        </div>
      </Box>

      {/* Selección de Oficina y Cobro */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
           <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="office">Seleccionar Oficina</InputLabel>
                <Select
                  label="Seleccionar Oficina"
                  labelId="office"
                  id="office"
                  value={office_id}
                  onChange={handleOfficeChange}
                >
                  {offices.map((office) => (
                    <MenuItem key={office.id} value={office.id}>
                      {office.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="collects">Seleccionar Cobro</InputLabel>
                <Select
                  label="Seleccionar Cobro"
                  labelId="collects"
                  id="collects"
                  value={collects}
                  onChange={handleCollectChange}
                >
                  {collectsOffice.map((collect) => (
                    <MenuItem key={collect.id} value={collect.id}>
                      {collect.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tabIndex} onChange={handleTabChange} aria-label="tabs">
        <Tab
          label={
            <Badge color="primary" badgeContent={routes.length} sx={{ '& .MuiBadge-badge': { backgroundColor: 'blue' } }}>
              En Ruta
            </Badge>
          }
        />
        <Tab
          label={
            <Badge color="secondary" badgeContent={pendingRoutes.length} sx={{ '& .MuiBadge-badge': { backgroundColor: 'orange' } }}>
              Pendiente
            </Badge>
          }
        />
        <Tab
          label={
            <Badge color="secondary" badgeContent={completedRoutes.length} sx={{ '& .MuiBadge-badge': { backgroundColor: 'green' } }}>
              Completado
            </Badge>
          }
        />
      </Tabs>

      {/* Contenido del Tab de Rutas */}
      {tabIndex === 0 && (
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              {routes.length > 0 ? (
                routes.map((route) => (
                  <Grid item xs={12} sm={6} md={4} key={route.lend.id}>
                    <Card sx={{ maxWidth: 500, padding: 2, borderRadius: 2, boxShadow: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <CardContent>
                        <Grid container spacing={1}>
                          <Grid item xs={3} container alignItems="center" justifyContent="center">
                            <Avatar sx={{ bgcolor: '#e0e7ff', width: 56, height: 56 }}>
                              {route.lend.client.fullname.charAt(0)}
                            </Avatar>
                          </Grid>

                          <Grid item xs={9}>
                            <Box display="flex" flexDirection="column">
                              <Typography variant="body1" fontWeight="bold">
                                {route.lend.client.fullname}
                              </Typography>
                              <Typography variant="body2">
                                <KeyIcon fontSize="small" /> {route.lend.client.idnumber}
                              </Typography>
                              <Typography variant="body2">
                                <AddIcCallIcon fontSize="small" /> {route.lend.client.phone}
                              </Typography>
                              <Typography variant="body2">
                                <PersonPinCircleIcon fontSize="small" /> {route.lend.client.mainaddress}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        <Box display="flex" flexDirection="column" textAlign="right" mt={2}>
                          {/* <Typography variant="body1">Recibo: #{route.lend.id}-{route.lend.id}</Typography> */}
                          <Typography variant="body1">
                            <CalendarTodayIcon fontSize="small" /> {obtenerFechaActual()}
                          </Typography>
                          <Typography variant="body2">Cuota: ${route.lend.daily_pay_value}</Typography>
                          <Typography variant="body2">Solicitado: ${route.lend.value}</Typography>
                          <Typography variant="body2">Total: ${route.lend.total_value}</Typography>
                        </Box>
                      </CardContent>

                      <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button variant="outlined" onClick={() => moveToPending(route)} style={{ color: "black" }}>
                          Pendiente
                        </Button>
                        {/* Botón para abrir el modal de pagar */}
                        <NavLink to="" onClick={() => handleOpenModal(route.lend.id)}>
                          <Button variant="contained" style={{ color: "white", marginLeft: '16px' }}>
                            Pagar
                          </Button>
                        </NavLink>
                      </Box>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No hay rutas disponibles.
                </Typography>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Contenido del Tab de Pendientes */}
      {tabIndex === 1 && (
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              {pendingRoutes.length > 0 ? (
                pendingRoutes.map((route) => (
                  <Grid item xs={12} sm={6} md={4} key={route.lend.id}>
                    <Card sx={{ maxWidth: 500, padding: 2, borderRadius: 2, boxShadow: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <CardContent>
                        <Grid container spacing={1}>
                          <Grid item xs={3} container alignItems="center" justifyContent="center">
                            <Avatar sx={{ bgcolor: '#e0e7ff', width: 56, height: 56 }}>
                              {route.lend.client.fullname.charAt(0)}
                            </Avatar>
                          </Grid>

                          <Grid item xs={9}>
                            <Box display="flex" flexDirection="column">
                              <Typography variant="body1" fontWeight="bold">
                                {route.lend.client.fullname}
                              </Typography>
                              <Typography variant="body2">
                                <KeyIcon fontSize="small" /> {route.lend.client.idnumber}
                              </Typography>
                              <Typography variant="body2">
                                <AddIcCallIcon fontSize="small" /> {route.lend.client.phone}
                              </Typography>
                              <Typography variant="body2">
                                <PersonPinCircleIcon fontSize="small" /> {route.lend.client.mainaddress}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        <Box display="flex" flexDirection="column" textAlign="right" mt={2}>
                          {/* <Typography variant="body1">Recibo: #{route.lend.id}-{route.lend.id}</Typography> */}
                          <Typography variant="body1">
                            <CalendarTodayIcon fontSize="small" /> {obtenerFechaActual()}
                          </Typography>
                          <Typography variant="body2">Cuota: ${route.lend.daily_pay_value}</Typography>
                          <Typography variant="body2">Solicitado: ${route.lend.value}</Typography>
                          <Typography variant="body2">Total: ${route.lend.total_value}</Typography>
                        </Box>
                      </CardContent>
                      <Box display="flex" justifyContent="flex-end" mt={2}>
                        {/* Botón para abrir el modal de pagar */}
                        <NavLink to="" onClick={() => handleOpenModal(route.lend.id)}>
                          <Button variant="contained" style={{ color: "white", marginLeft: '16px' }}>
                            Pagar
                          </Button>
                        </NavLink>
                      </Box>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Typography>No hay tarjetas pendientes</Typography>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Contenido del Tab de Completado */}
      {tabIndex === 2 && (
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              {completedRoutes.length > 0 ? (
                completedRoutes.map((route) => (
                  <Grid item xs={12} sm={6} md={4} key={route.lend.id}>
                    <Card sx={{ maxWidth: 500, padding: 2, borderRadius: 2, boxShadow: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <CardContent>
                        <Grid container spacing={1}>
                          <Grid item xs={3} container alignItems="center" justifyContent="center">
                            <Avatar sx={{ bgcolor: '#e0e7ff', width: 56, height: 56 }}>
                              {route.lend.client.fullname.charAt(0)}
                            </Avatar>
                          </Grid>

                          <Grid item xs={9}>
                            <Box display="flex" flexDirection="column">
                              <Typography variant="body1" fontWeight="bold">
                                {route.lend.client.fullname}
                              </Typography>
                              <Typography variant="body2">
                                <KeyIcon fontSize="small" /> {route.lend.client.idnumber}
                              </Typography>
                              <Typography variant="body2">
                                <AddIcCallIcon fontSize="small" /> {route.lend.client.phone}
                              </Typography>
                              <Typography variant="body2">
                                <PersonPinCircleIcon fontSize="small" /> {route.lend.client.mainaddress}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        <Box display="flex" flexDirection="column" textAlign="right" mt={2}>
                          <Typography variant="body1">Recibo: #{route.lend.id}-</Typography>
                          <Typography variant="body1">
                            <CalendarTodayIcon fontSize="small" /> {obtenerFechaActual()}
                          </Typography>
                          <Typography variant="body2">Cuota: ${route.lend.daily_pay_value}</Typography>
                          <Typography variant="body2">Solicitado: ${route.lend.value}</Typography>
                          <Typography variant="body2">Total: ${route.lend.total_value}</Typography>
                        </Box>
                      </CardContent>
                      <Box display="flex" justifyContent="flex-end" mt={2}>
                        {/* Botón para abrir el modal de pagar */}
                        <NavLink
                          to=""
                          onClick={() => handleOpenInvoiceModal(route.lend.client.fullname, route.lend.id)} // <-- Pasa el payId aquí
                        >
                          <Button variant="contained" style={{ color: "white", marginLeft: '16px' }}>
                            Imprimir
                          </Button>
                        </NavLink>
                      </Box>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Typography>No hay tarjetas completadas</Typography>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Modal para pagos */}
      <MyModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onButtonClick={() => handleCloseModal()}
        id={selectedPaymentId?.toString() ?? ''}// Proporciona el id seleccionado
      />
      <Modal
        open={modalInvoiceOpen}
        onClose={handleCloseInvoiceModal}
        aria-labelledby="invoice-modal-title"
        aria-describedby="invoice-modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300
        }}
      >
        <Box sx={style}>
          <Invoice showAlert={showAlert} showLoading={showLoading} hideLoading={hideLoading}
            isOpen={modalInvoiceOpen}
            onClose={handleCloseInvoiceModal}
            clientName={clientFullName} // Proporciona el nombre del cliente al modal
            payId={selectedPaymentId ?? 1}
          />
        </Box>
      </Modal>
    </HeaderResponsive>
  );
};

export default RutasOffList;
